/**
 * Uniform in-page help (CF help icons): a small "?" icon that opens a popover with the feature's HELP.md.
 *
 *   import help from "./HELP.md?raw";
 *   <PageHeader title="Sales orders" help={help} />          // preferred: next to the page title
 *   <HelpButton content={help} title="BOM editor" />         // standalone, for embedded components
 *
 * HELP.md files are short markdown: a heading, a "Use the … to …" paragraph, and "How to use" steps.
 */
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { Box, IconButton, Popover, Tooltip } from "@mui/material";
import DOMPurify from "dompurify";
import { marked } from "marked";
import { useMemo, useState, type MouseEvent } from "react";

export function HelpButton({ content, title, size = "small" }: { content: string; title?: string; size?: "small" | "medium" }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const html = useMemo(() => DOMPurify.sanitize(marked.parse(content, { async: false }) as string), [content]);
  const label = title ? `Help: ${title}` : "Help";
  return (
    <>
      <Tooltip title="About this page">
        <IconButton size={size} aria-label={label} aria-haspopup="dialog" aria-expanded={!!anchor}
          onClick={(e: MouseEvent<HTMLElement>) => setAnchor(e.currentTarget)}
          sx={{ color: "text.secondary", "&:hover": { color: "primary.main" } }}>
          <HelpOutlineIcon fontSize={size === "small" ? "small" : "medium"} />
        </IconButton>
      </Tooltip>
      <Popover open={!!anchor} anchorEl={anchor} onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }} transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { role: "dialog", "aria-label": label, sx: { width: 420, maxWidth: "calc(100vw - 32px)", maxHeight: "70vh", p: 2.5 } } }}>
        <Box className="help-content" dangerouslySetInnerHTML={{ __html: html }} sx={{
          fontSize: 14, lineHeight: 1.55, color: "text.primary",
          "& h1": { fontSize: 18, fontWeight: 600, m: 0, mb: 1 },
          "& h2": { fontSize: 14, fontWeight: 600, mt: 2, mb: 0.5, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.4 },
          "& h3": { fontSize: 14, fontWeight: 600, mt: 1.5, mb: 0.5 },
          "& p": { m: 0, mb: 1 },
          "& ul, & ol": { m: 0, mb: 1, pl: 2.5 },
          "& li": { mb: 0.5 },
          "& code": { fontSize: "0.9em", bgcolor: "action.hover", px: 0.5, borderRadius: 0.5 },
          "& a": { color: "primary.main" },
          "& > :last-child": { mb: 0 },
        }} />
      </Popover>
    </>
  );
}
